import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material';
import {
  FormatBoldRounded,
  FormatItalicRounded,
  FormatUnderlinedRounded,
  CodeRounded,
  LinkRounded,
  FormatAlignLeftRounded,
  FormatAlignCenterRounded,
  FormatAlignRightRounded,
  FormatListBulletedRounded,
  FormatListNumberedRounded,
  ChecklistRounded,
  BorderColorRounded,
  ExpandMoreRounded
} from '@mui/icons-material';
import { borderRadius, fonts, colors } from '../../../styles/theme';
import { formFieldStyles } from '../../../styles/formFieldStyles';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';

interface TextEditorFieldProps {
  value: string;
  placeholder?: string;
  label: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  onChange?: (value: string) => void;
  /** Beim Verlassen des Editors, mit seinem Inhalt – zum Speichern ohne Knopf (ADR 004) */
  onBlur?: (value: string) => void;
  labelBackgroundColor?: string;
  error?: string;
  sx?: any;
  minHeight?: string;
  noBorder?: boolean;
  /**
   * Layout-Variante:
   * 'default' – Label innerhalb des Containers (Standard)
   * 'floating' – Floating-Label auf dem oberen Rand (für Dialoge),
   *   bleibt beim Scrollen sichtbar
   */
  variant?: 'default' | 'floating';
}

// Toolbar-Komponente
const Toolbar: React.FC<{ editor: Editor | null; readOnly?: boolean }> = ({ editor, readOnly = false }) => {
  const [listMenuAnchor, setListMenuAnchor] = useState<null | HTMLElement>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const listMenuOpen = Boolean(listMenuAnchor);

  const handleListMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setListMenuAnchor(event.currentTarget);
  };

  const handleListMenuClose = () => {
    setListMenuAnchor(null);
  };

  const handleLinkDialogOpen = () => {
    setLinkDialogOpen(true);
    setLinkUrl('');
  };

  const handleLinkDialogClose = () => {
    setLinkDialogOpen(false);
    setLinkUrl('');
  };

  const handleLinkSubmit = () => {
    if (linkUrl && editor) {
      // Prüfe ob eine URL ausgewählt ist
      if (editor.isActive('link')) {
        // Link bearbeiten
        editor.chain().focus().setLink({ href: linkUrl }).run();
      } else {
        // Neuen Link erstellen
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
      handleLinkDialogClose();
    }
  };

  const handleRemoveLink = () => {
    if (editor) {
      editor.chain().focus().unsetLink().run();
      handleLinkDialogClose();
    }
  };

  if (readOnly || !editor) {
    return null;
  }

  return (
    <Box sx={{
      ...formFieldStyles.toolbar,
      borderTopLeftRadius: `${borderRadius.field}px`,
      borderTopRightRadius: `${borderRadius.field}px`,
    }}>
      {/* Text-Formatierung */}
      <Tooltip title="Fett">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive('bold') ? 'primary' : 'default'}
        >
          <FormatBoldRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Kursiv">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive('italic') ? 'primary' : 'default'}
        >
          <FormatItalicRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Unterstrichen">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          color={editor.isActive('underline') ? 'primary' : 'default'}
        >
          <FormatUnderlinedRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Code */}
      <Tooltip title="Code">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleCode().run()}
          color={editor.isActive('code') ? 'primary' : 'default'}
        >
          <CodeRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Link */}
      <Tooltip title="Link einfügen">
        <IconButton
          size="small"
          onClick={handleLinkDialogOpen}
          color={editor.isActive('link') ? 'primary' : 'default'}
        >
          <LinkRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Link-Dialog */}
      <Dialog open={linkDialogOpen} onClose={handleLinkDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editor?.isActive('link') ? 'Link bearbeiten' : 'Link einfügen'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleLinkSubmit();
              }
              if (e.key === 'Escape') {
                handleLinkDialogClose();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          {editor?.isActive('link') && (
            <Button onClick={handleRemoveLink} color="error">
              Link entfernen
            </Button>
          )}
          <Button onClick={handleLinkDialogClose}>
            Abbrechen
          </Button>
          <Button onClick={handleLinkSubmit} variant="contained" disabled={!linkUrl.trim()}>
            {editor?.isActive('link') ? 'Aktualisieren' : 'Einfügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ausrichtung */}
      <Tooltip title="Links ausrichten">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
        >
          <FormatAlignLeftRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Zentriert">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
        >
          <FormatAlignCenterRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Rechts ausrichten">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
        >
          <FormatAlignRightRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Listen - Dropdown-Menü */}
      <Tooltip title="Liste">
        <IconButton
          size="small"
          onClick={handleListMenuOpen}
          color={
            editor.isActive('bulletList') ||
              editor.isActive('orderedList') ||
              editor.isActive('taskList')
              ? 'primary'
              : 'default'
          }
        >
          <FormatListBulletedRounded fontSize="small" />
          <ExpandMoreRounded fontSize="small" sx={{ ml: -0.5, fontSize: '0.75rem' }} />
        </IconButton>
      </Tooltip>

      {/* Listen-Dropdown-Menü */}
      <Menu
        anchorEl={listMenuAnchor}
        open={listMenuOpen}
        onClose={handleListMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleBulletList().run();
            handleListMenuClose();
          }}
          selected={editor.isActive('bulletList')}
        >
          <ListItemIcon>
            <FormatListBulletedRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Bullet List</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleOrderedList().run();
            handleListMenuClose();
          }}
          selected={editor.isActive('orderedList')}
        >
          <ListItemIcon>
            <FormatListNumberedRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ordered List</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().toggleTaskList().run();
            handleListMenuClose();
          }}
          selected={editor.isActive('taskList')}
        >
          <ListItemIcon>
            <ChecklistRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Task List</ListItemText>
        </MenuItem>
      </Menu>

      {/* Highlight */}
      <Tooltip title="Hervorheben">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          color={editor.isActive('highlight') ? 'primary' : 'default'}
        >
          <BorderColorRounded fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Hauptkomponente
const TextEditorField: React.FC<TextEditorFieldProps> = ({
  value,
  placeholder = '',
  label,
  readOnly = false,
  fullWidth = true,
  onChange,
  onBlur,
  labelBackgroundColor = colors.background.default,
  error,
  sx = {},
  minHeight = '100px',
  noBorder = false,
  variant = 'default',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Deaktiviere einige Standard-Erweiterungen für eine minimalistische Version
        heading: false,
        horizontalRule: false,
        codeBlock: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TaskList,
      TaskItem,
      Highlight,
    ],
    content: value || '',
    editable: !readOnly,
  });

  // Aktualisiere den Editor-Inhalt wenn sich der value-Prop ändert
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  // Handle content changes - nur wenn nicht readOnly
  useEffect(() => {
    if (editor && onChange && !readOnly) {
      const handleUpdate = () => {
        const html = editor.getHTML();
        onChange(html);
      };

      editor.on('update', handleUpdate);

      return () => {
        editor.off('update', handleUpdate);
      };
    }
  }, [editor, onChange, readOnly]);

  useEffect(() => {
    if (editor && onBlur && !readOnly) {
      const handleBlur = () => onBlur(editor.getHTML());
      editor.on('blur', handleBlur);
      return () => {
        editor.off('blur', handleBlur);
      };
    }
  }, [editor, onBlur, readOnly]);

  // Zusätzliche Sicherheit: Stelle sicher, dass der Editor im Readonly-State nicht bearbeitbar ist
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  const isFloating = variant === 'floating';

  const editorContent = (
    <>
      <Toolbar editor={editor} readOnly={readOnly} />
      <Box sx={{
        position: 'relative',
        padding: '16px',
        paddingTop: isFloating ? '16px' : '24px',
        borderBottomLeftRadius: `${borderRadius.field}px`,
        borderBottomRightRadius: `${borderRadius.field}px`,
      }}>
        <EditorContent
          editor={editor}
          style={{
            padding: 0,
            font: '500 0.875rem inherit',
            border: 'none',
            backgroundColor: 'transparent',
            minHeight: minHeight,
          } as React.CSSProperties}
        />
        {!readOnly && placeholder && !value && (
          <Box
            sx={{
              position: 'absolute',
              top: isFloating ? '16px' : '24px',
              left: '16px',
              color: 'text.disabled',
              pointerEvents: 'none',
              font: '500 0.875rem inherit',
            }}
          >
            {placeholder}
          </Box>
        )}
        <style>
          {`
            .tiptap {
              border: none !important;
              background: transparent !important;
              box-shadow: none !important;
              outline: none !important;
              min-height: ${minHeight};
            }
            .tiptap p {
              margin: 0 0 8px 0 !important;
              padding: 0 !important;
              border: none !important;
              background: transparent !important;
            }
            .tiptap:focus {
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
            }
            .tiptap ul,
            .tiptap ol {
              padding-left: 24px !important;
              margin: 8px 0 !important;
            }
            .tiptap ul {
              list-style-type: disc !important;
            }
            .tiptap ol {
              list-style-type: decimal !important;
            }
            .tiptap li {
              margin: 4px 0 !important;
              padding-left: 4px !important;
            }
            .tiptap ul[data-type="taskList"] {
              list-style: none !important;
              padding-left: 0 !important;
            }
            .tiptap ul[data-type="taskList"] li {
              display: flex !important;
              align-items: flex-start !important;
              padding-left: 0 !important;
            }
            .tiptap ul[data-type="taskList"] li > label {
              flex: 0 0 auto !important;
              margin-right: 8px !important;
            }
            .tiptap a {
              color: #1976d2 !important;
              text-decoration: underline !important;
              cursor: pointer !important;
            }
            .tiptap a:hover {
              color: #1565c0 !important;
            }
            .tiptap mark {
              background-color: #ffeb3b !important;
              color: inherit !important;
              padding: 2px 4px !important;
              border-radius: 2px !important;
            }
            .tiptap strong {
              font-weight: 700 !important;
            }
            .tiptap em {
              font-style: italic !important;
            }
            .tiptap u {
              text-decoration: underline !important;
            }
            .tiptap code {
              background-color: #f5f5f5 !important;
              padding: 2px 6px !important;
              border-radius: 4px !important;
              font-family: 'Courier New', monospace !important;
              font-size: 0.85em !important;
            }
          `}
        </style>
      </Box>
    </>
  );

  const containerBorderSx = {
    borderRadius: `${borderRadius.field}px`,
    border: `1px solid ${error ? colors.status.error.main : colors.border.default}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    backgroundColor: 'white',
    transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
      borderColor: error ? colors.status.error.main : colors.border.focus,
    },
    '&:focus-within': {
      borderColor: error ? colors.status.error.main : colors.border.focus,
      boxShadow: error
        ? `0 4px 12px ${colors.shadow.error}`
        : '0 4px 12px rgba(25, 118, 210, 0.15)',
    },
  };

  if (isFloating) {
    return (
      <Box sx={{ ...formFieldStyles.container, width: fullWidth ? '100%' : 'auto', position: 'relative', ...sx }}>
        {label && (
          <Typography
            sx={{
              position: 'absolute',
              top: -9,
              left: 16,
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: '0.75rem',
              color: error ? colors.status.error.main : colors.text.secondary,
              zIndex: 2,
            }}
          >
            {label}
          </Typography>
        )}
        <Box sx={containerBorderSx}>
          {editorContent}
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontSize: '0.75rem', fontWeight: 400, textAlign: 'right' }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ ...formFieldStyles.container, width: fullWidth ? '100%' : 'auto', ...sx }}>
      <Box
        sx={{
          borderRadius: noBorder ? 0 : `${borderRadius.field}px`,
          boxShadow: noBorder ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.08)',
          transition: noBorder ? 'none' : 'box-shadow 0.2s ease-in-out',
          border: noBorder ? 'none' : `1px solid ${error ? colors.status.error.main : colors.border.default}`,
          backgroundColor: 'white',
          overflow: 'hidden',
          ...(!noBorder && {
            '&:hover': {
              boxShadow: `0 4px 8px ${colors.shadow.medium}`,
              borderColor: error ? colors.status.error.main : colors.border.focus,
            },
            '&:focus-within': {
              boxShadow: error ? `0 4px 12px ${colors.shadow.error}` : `0 4px 12px ${colors.shadow.focus}`,
              borderColor: error ? colors.status.error.main : colors.border.focus,
              borderWidth: 2,
            },
          }),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: error ? colors.status.error.main : (value ? colors.text.primary : colors.text.disabled),
            backgroundColor: labelBackgroundColor,
            padding: '0 8px',
            fontWeight: 400,
            fontSize: '0.75rem',
            marginLeft: '14px',
            marginTop: '-8px',
            position: 'absolute',
            zIndex: 1,
            '&.Mui-focused': {
              color: error ? colors.status.error.main : colors.border.focus,
            },
          }}
        >
          {label}
        </Typography>
        {editorContent}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontSize: '0.75rem', fontWeight: 400, textAlign: 'right' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default TextEditorField;
